import React, { useEffect, useState } from "react";
import { Icon } from "../../../../../core/components/atoms/Icon";
import { Typography } from "../../../../../core/components/molecules/Typography";
import PeriodSelector from "../PeriodSelector";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import DisciplineReportCard from "../../common/DisciplineReportCard";
import { useGetHubReport } from "../../../hooks/ScheduleHub/useGetHubReport";
import { HubReport, TPeriodSelector } from "../../../model/ScheduleHub";
import { getDisciplineIcon } from "../../../../../core/utils/discipline";
import BrainLoadingWrapper from "../../../../../core/components/molecules/BrainLoading";
import { ScheduleStartInfo } from "../../../utils/scheduleStart";

const NAV_PREV_CLASS = 'discipline-report-swiper-prev';
const NAV_NEXT_CLASS = 'discipline-report-swiper-next';

export type DisciplineItem = {
  discipline: string;
  progress: number;
  icon: {
    type: 'svg' | 'material';
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | string;
  };
  doneItems: { type: 'exercises' | 'lessons' | 'materials'; value: number }[];
};

interface IDisciplineReport {
  cronogramaId?: string;
  startInfo: ScheduleStartInfo;
}

const DisciplineReport: React.FC<IDisciplineReport> = ({ cronogramaId, startInfo }) => {
  const { mutate, isPending, isError, data } = useGetHubReport();

  const [period, setPeriod] = useState<TPeriodSelector>('week');
  const [search, setSearch] = useState('');
  const [reports, setReports] = useState<HubReport[]>([]);

  useEffect(() => {
    if(cronogramaId) {
      mutate({ idCronograma: cronogramaId, period });
    }
  }, [cronogramaId, period]);

  useEffect(() => {
    if (data) {
      setReports(data);
    }
  }, [data]);

  const onChangeSearch = (search: string) => {
    if (search.length === 0) {
      setSearch('');
      setReports(data || []);
      return;
    }

    setSearch(search);
    setReports(
      data?.filter((report) =>
        report.area.toLowerCase().includes(search.toLowerCase())
      ) || []
    );
  };

  const showContent = () => {
    if(isPending || !cronogramaId) {
      return <BrainLoadingWrapper size="sm"/>;
    }

    if (isError || !data || data.length === 0 || (startInfo.status !== 'onTime' && period === 'week')) {
      return (
        <div className='flex flex-col items-center justify-center gap-4 py-4'>
          <Typography
            type='p'
            className='text-md font-normal italic text-[#FFF]'
          >
            Este resumo ficará disponível após suas primeiras interações com
            seus conteúdos e atividades.
          </Typography>
        </div>
      );
    }

    return (
      <div className='relative flex w-full items-center gap-1'>
        {reports.length > 1 && (
          <button
            type='button'
            aria-label='Disciplina anterior'
            className={`${NAV_PREV_CLASS} rounded-full hover:bg-white/10 flex h-9 min-h-8 w-9 min-w-8 flex-shrink-0 cursor-pointer items-center justify-center text-mono-color-white transition-colors disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <Icon
              Icon='navigate_before'
              type='material'
              className='text-[20px]'
            />
          </button>
        )}
        <Swiper
          modules={[Autoplay, Navigation]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          loop={reports.length > 1}
          slidesPerView={1}
          spaceBetween={20}
          navigation={
            reports.length > 1
              ? {
                  prevEl: `.${NAV_PREV_CLASS}`,
                  nextEl: `.${NAV_NEXT_CLASS}`,
                }
              : false
          }
          pagination={false}
          className='w-full min-w-0 flex-1'
        >
          {reports.map((report) => (
            <SwiperSlide key={report.area}>
              <DisciplineReportCard
                discipline={report.area}
                progress={report.progress}
                icon={{ type: 'svg', Icon: getDisciplineIcon(report.area) }}
                doneItems={[
                  { type: 'exercises', value: report.exercises },
                  { type: 'lessons', value: report.lessons },
                  { type: 'materials', value: report.materials },
                  { type: 'essays', value: report.essays },
                ]}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {reports.length > 1 && (
          <button
            type='button'
            aria-label='Próxima disciplina'
            className={`${NAV_NEXT_CLASS} rounded-full hover:bg-white/10 flex h-9 min-h-8 w-9 min-w-8 flex-shrink-0 cursor-pointer items-center justify-center text-mono-color-white transition-colors disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <Icon
              Icon='navigate_next'
              type='material'
              className='text-[20px]'
            />
          </button>
        )}
      </div>
    );
  };

  return (
    <div data-tour="discipline-report" className='flex h-[315px] flex-col justify-between rounded-lg bg-brand-color-secondary-pure p-5 md:h-[275.5px]'>
      <div className='mb-[2px] flex flex-col justify-between gap-md md:flex-row md:items-center md:gap-0'>
        <Typography type='title' size='h3' className='text-[#fff]'>
          Resumo das Disciplinas
        </Typography>
        <div className='flex w-full flex-row items-center gap-sm rounded-md bg-[#D0350E] px-2 py-1 md:w-[150px]'>
          <input
            type='text'
            className='w-full border-none bg-[transparent] p-0 text-sm text-[#fff] outline-none placeholder:text-[#fff] focus:border-none focus:outline-none focus:ring-0'
            placeholder='Disciplinas'
            value={search}
            onChange={(e) => onChangeSearch(e.target.value)}
          />
          <Icon
            Icon='search'
            type='material'
            className='text-[16px] text-[#fff]'
          />
        </div>
      </div>
      {showContent()}
      <PeriodSelector
        centralized={true}
        period={period}
        backgroundColor='bg-[#D0350E]' // A little more darker than #D0350E
        buttonColorSchema={{
          textColor: "!text-[#dcdcdc]",
          backgroundColor: "bg-transparent",
          selectedBackgroundColor: "bg-[#fff]",
          selectedTextColor: "text-brand-color-secondary-pure"
        }}
        onChange={(period) => setPeriod(period)}
      />
    </div>
  );
};

export default DisciplineReport;
